import React from "react";
import clsx from "clsx";
import { AnciPassengerVM } from "../viewModels";
import { AnciService } from "../../../state/models/Ancillary";
import Icon from "react-icons-kit/Icon";

import { userCircleO } from "react-icons-kit/fa/userCircleO";
import { suitcase } from "react-icons-kit/fa/suitcase";
import { ls } from "../../../locale";
import { CheckRadio } from "../../../comps/checkbox";

export function PassengerBox({
  passenger,
  passengerIndex,  // the passenger index in the passengers list
  currencyCode,
  flightId,
  onSelectBag,
  onClearBag
}: {
  passenger: AnciPassengerVM,
  passengerIndex: number,
  currencyCode: string,
  flightId: number,
  onSelectBag: (flightId: number, passengerId: number, ssrCode: string) => void,
  onClearBag: (flightId: number, passengerId: number, ssrCode: string) => void
}) {

  const handleClick = (ssrCode: string, action: BaggageSelectAction, passenger: AnciPassengerVM) => {
    if (action === 'select') {
      onSelectBag(flightId, passenger.id, ssrCode);
    } else if (action === 'clear') {
      onClearBag(flightId, passenger.id, ssrCode);
    }
  };

  const rPaxType = ls.t('PaxType');
  const passengerName = passenger.lastName 
            ? `${passenger.firstName} ${passenger.lastNextraame}` 
            : `${rPaxType[passenger.paxTypeName].Passenger} ${passenger.paxIndex}`;

  const rExtraPage = ls.t('ExtraPage');

  let additionalMessageForBag: string | undefined;
  let bagIndexForAdditionalMessage = -1;
  if(passengerIndex === 0) { 
    bagIndexForAdditionalMessage = Array.isArray(passenger.baggages) ? passenger.baggages.findIndex(bag => bag.included) : -1;
    if(bagIndexForAdditionalMessage >= 0){
      bagIndexForAdditionalMessage += 1;
      additionalMessageForBag = rExtraPage.AdditionalCheckedBaggage;
    }
}

  return (
    <div className='md:tw-shadow-outline-gray tw-mt-1 tw-bg-white tw-mb-6'>
      <div className={clsx('tw-flex tw-flex-col md:tw-flex-row md:tw-items-stretch md:tw-justify-between',
        'tw-border tw-border-solid tw-border-gray-200',
        'tw-shadow-outline-gray md:tw-shadow-none',
        'tw-min-h-[125px]',
      )}>
        <div className="tw-p-6 tw-flex tw-flex-row tw-gap-3 tw-items-center md:tw-min-w-[300px]">
          <Icon icon={userCircleO} className="passenger-icon" />
          <span>{passengerName}</span>
        </div>
        <div className="tw-flex tw-flex-col md:tw-flex-row">
          {Array.isArray(passenger.baggages) && passenger.baggages.map((bag, index) => {            

            const additionalMessage = index === bagIndexForAdditionalMessage ? additionalMessageForBag : undefined;

            return (
              <BaggageBox key={index}
                baggage={bag}
                currencyCode={currencyCode}
                onClick={(ssrCode, action) => handleClick(ssrCode, action, passenger)}
                additionalMessage={additionalMessage}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

type BaggageSelectAction = 'select' | 'clear' | null;

function BaggageBox({
  baggage,
  currencyCode,
  onClick,
  additionalMessage
}: {
  baggage: AnciService,
  currencyCode: string,
  onClick: (ssrCode: string, action: BaggageSelectAction) => void,
  additionalMessage?: string
}) {

  const rCommon = ls.t('Common');
  const rExtraPage = ls.t('ExtraPage');

  
  const borderPosition = ls.t('rootClass') === 'ar'
    ? "md:tw-border-r"
    : "md:tw-border-l";

  const borderStyle = baggage.selected
    ? "tw-border-[3px] tw-border-solid tw-border-blue-sa"
    : "tw-border-0 tw-border-t tw-border-solid tw-border-gray-200 md:tw-border-t-0 " + borderPosition;

  let action: BaggageSelectAction = null;

  if (!(baggage.included || baggage.purchased)) {
    if (baggage.selected) {
      action = 'clear';
    }
    else if (baggage.quantity > 0) {
      action = 'select';
    }
  }

  return (
    <div className={clsx("tw-flex tw-flex-row tw-justify-between md:tw-justify-center tw-items-center tw-gap-2 tw-p-5",
        "md:tw-flex-col md:tw-p-2 md:tw-pb-1 md:tw-min-w-40 md:tw-gap-0", 
        "tw-box-border",
        "tw-relative", // for the additional notes tag
        borderStyle)}
      onClick={() => { action && onClick(baggage.ssrCode, action) }}>

      {baggage.included || baggage.purchased // show included tag in mobile view
        ? <>
            <div className="md:tw-hidden"><span className="included">{baggage.included ? rCommon.Included : rCommon.Purchased}</span></div>
            <div className="tw-hidden md:tw-block tw-h-7">{/* for layout spacing */}</div>
          </>
        : <PriceBox amount={baggage.amount} currencyCode={currencyCode} />
      }
      {baggage.included  // show additional notes for included baggage
        ? <div className="md:tw-hidden">{rExtraPage.CheckedBaggage}</div>
        : null
      }
      <div className="tw-flex tw-flex-row tw-gap-2 tw-items-center md:tw-flex-col md:tw-gap-2">
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
          <span className="tw-text-[20px]">{baggage.ssrCode && !baggage.included ? '+' : ''} {baggage.label}</span>
          <Icon icon={suitcase} className="baggage-icon" />
        </div>

        {!(baggage.included || baggage.purchased) && (
          (baggage.quantity > 0 || baggage.selected) 
          ? <CheckRadio checked={baggage.selected} color="blue" />
          : <span>{rCommon.NotAvailable}</span>
        )}

        {baggage.included && <div className="tw-hidden md:tw-block"><span className="included">{rCommon.Included}</span></div>}
        {baggage.purchased && <div className="tw-hidden md:tw-block"><span className="included">{rCommon.Purchased}</span></div>}
      </div>
    </div>
  )
}

function PriceBox({ amount, currencyCode }: { amount: number, currencyCode: string }) {
  return (
    <div className="tw-inline-block sa-text-green">
      <span className="tw-text-[10px]">{currencyCode}</span>
      <span className=" tw-text-[20px] tw-px-2 tw-font-bold">{amount}</span>
    </div>
  )
}